import React from 'react';
import Bell from 'lucide-react/dist/esm/icons/bell.js';
import LogIn from 'lucide-react/dist/esm/icons/log-in.js';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useDrawsPage } from './DrawsPageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useAuthConfig } from '../../../contexts/AuthConfigContext';

const PREFS_STORAGE_KEY = 'account_preferences';

function getNotificationsEnabled() {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return false;
    const prefs = JSON.parse(raw);
    return !!(prefs.emailUpdates || prefs.smsReminders);
  } catch {
    return false;
  }
}

export default function DrawsLiveNow() {
  const { t } = useTranslations();
  const { activeTab } = useDrawsPage();
  const { isLoggedIn } = useAuth();
  const { isGuestCheckoutEnabled } = useAuthConfig();
  const navigate = useNavigate();

  if (activeTab !== 'upcoming') return null;

  // Guest checkout users can't set up account-based notifications; a sign-in CTA
  // here contradicts the guest purchase flow, so hide the banner entirely.
  if (!isLoggedIn && isGuestCheckoutEnabled()) return null;

  const notificationsEnabled = isLoggedIn && getNotificationsEnabled();

  // Hide the section if the user is logged in and already has notifications enabled
  if (notificationsEnabled) return null;

  const handleClick = () => {
    if (!isLoggedIn) {
      // Dispatch event to open login modal from App.jsx
      window.dispatchEvent(new CustomEvent('draws:requestLogin'));
    } else {
      // Logged in but notifications off — navigate to account preferences
      navigate('/account?section=preferences');
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 mb-8 shadow-xl text-white w-full md:w-1/2 mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <Bell className="w-6 h-6" />
        <h2 className="text-2xl font-bold">{t('draws.notifyBanner.title')}</h2>
      </div>
      <p className="text-blue-100 mb-6 max-w-2xl">
        {isLoggedIn
          ? t('draws.notifyBanner.bodyLoggedIn')
          : t('draws.notifyBanner.body')}
      </p>
      <button
        onClick={handleClick}
        className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all hover:shadow-lg hover:-translate-y-0.5 flex items-center gap-2"
      >
        {!isLoggedIn && <LogIn className="w-5 h-5" />}
        {isLoggedIn
          ? t('draws.notifyBanner.buttonManage')
          : t('draws.notifyBanner.buttonLogin')}
      </button>
    </div>
  );
}
